import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  Clock, 
  ExternalLink, 
  Ticket,
  Navigation,
  Building2,
  UtensilsCrossed,
  Landmark,
  Baby,
  ArrowLeft,
  Home,
  Star,
  Map,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyLocalGuide = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/properties/${id}`);
        if (!response.ok) throw new Error('Property not found');
        const data = await response.json();
        setProperty({
          ...data,
          pricePerNight: data.price_per_night,
          bookingUrl: data.booking_url,
          bedConfiguration: data.bed_configuration
        });
      } catch (err) {
        console.error('Error fetching property:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  // Determine location type based on property address
  const isCapitolHill = property?.location?.includes('Capitol Hill') || property?.location?.includes('14th Street SE');

  const categories = [
    { id: 'all', name: 'All', icon: MapPin },
    { id: 'restaurants', name: 'Restaurants', icon: UtensilsCrossed },
    { id: 'museums', name: 'Museums', icon: Building2 },
    { id: 'sightseeing', name: 'Sightseeing', icon: Landmark },
    { id: 'family', name: 'Family-Friendly', icon: Baby },
  ];

  // Capitol Hill area recommendations
  const capitolHillPlaces = [
    // Restaurants
    {
      name: "Ted's Bulletin",
      description: 'Classic American comfort food with famous homemade Pop-Tarts and all-day breakfast.',
      distance: '2 min walk',
      category: 'restaurants',
      highlight: 'Local Favorite',
      website: 'https://tedsbulletin.com/',
      directions: 'https://maps.google.com/?q=Teds+Bulletin+Capitol+Hill+Washington+DC'
    },
    {
      name: 'Barracks Row',
      description: 'Historic stretch of 8th Street SE with dozens of restaurants, cafes, and bars.',
      distance: '5 min walk',
      category: 'restaurants',
      highlight: 'Restaurant Row',
      website: 'https://www.barracksrow.org/',
      directions: 'https://maps.google.com/?q=Barracks+Row+Washington+DC'
    },
    {
      name: 'Eastern Market',
      description: 'Historic public market with fresh produce, artisan goods, and weekend flea market.',
      distance: '3 min walk',
      category: 'restaurants',
      highlight: 'Must Visit',
      website: 'https://easternmarket-dc.org/',
      directions: 'https://maps.google.com/?q=Eastern+Market+Washington+DC'
    },
    {
      name: 'Belga Café',
      description: 'Belgian cuisine with excellent mussels, frites, and brunch options.',
      distance: '3 min walk',
      category: 'restaurants',
      website: 'https://belgacafe.com/',
      directions: 'https://maps.google.com/?q=Belga+Cafe+Washington+DC'
    },
    {
      name: 'Good Stuff Eatery',
      description: "Gourmet burgers by Top Chef's Spike Mendelsohn.",
      distance: '5 min walk',
      category: 'restaurants',
      website: 'https://www.goodstuffeatery.com/',
      directions: 'https://maps.google.com/?q=Good+Stuff+Eatery+Capitol+Hill'
    },
    // Sightseeing
    {
      name: 'U.S. Capitol Building',
      description: 'Home of Congress with stunning architecture and free guided tours available.',
      distance: '10 min walk',
      category: 'sightseeing',
      highlight: 'Iconic Landmark',
      tickets: 'https://www.visitthecapitol.gov/plan-visit',
      directions: 'https://maps.google.com/?q=US+Capitol+Building+Washington+DC'
    },
    {
      name: 'Library of Congress',
      description: "World's largest library with stunning architecture and free tours.",
      distance: '12 min walk',
      category: 'sightseeing',
      highlight: 'Must See',
      tickets: 'https://www.loc.gov/visit/',
      directions: 'https://maps.google.com/?q=Library+of+Congress+Washington+DC'
    },
    {
      name: 'Supreme Court',
      description: 'Highest court in the United States. Public sessions available.',
      distance: '12 min walk',
      category: 'sightseeing',
      website: 'https://www.supremecourt.gov/visiting/visiting.aspx',
      directions: 'https://maps.google.com/?q=Supreme+Court+Washington+DC'
    },
    {
      name: 'Lincoln Park',
      description: 'Beautiful neighborhood park with the Emancipation Memorial.',
      distance: '5 min walk',
      category: 'family',
      directions: 'https://maps.google.com/?q=Lincoln+Park+Washington+DC'
    },
    // Museums
    {
      name: 'Folger Shakespeare Library',
      description: "World's largest Shakespeare collection with beautiful Elizabethan garden.",
      distance: '10 min walk',
      category: 'museums',
      tickets: 'https://www.folger.edu/visit',
      directions: 'https://maps.google.com/?q=Folger+Shakespeare+Library+Washington+DC'
    },
    {
      name: 'National Air and Space Museum',
      description: 'FREE Smithsonian museum with aircraft, spacecraft, and aviation history.',
      distance: '15 min walk',
      category: 'museums',
      highlight: 'Free Entry',
      tickets: 'https://airandspace.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Air+and+Space+Museum+Washington+DC'
    },
    {
      name: 'National Museum of Natural History',
      description: 'FREE museum featuring the Hope Diamond, dinosaurs, and more.',
      distance: '18 min walk',
      category: 'museums',
      highlight: 'Free Entry',
      tickets: 'https://naturalhistory.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Museum+of+Natural+History+Washington+DC'
    },
    // Family
    {
      name: 'Capitol Hill Playground',
      description: 'Well-maintained playground perfect for kids with nearby benches for parents.',
      distance: '4 min walk',
      category: 'family',
      directions: 'https://maps.google.com/?q=Capitol+Hill+playground+Washington+DC'
    },
    {
      name: 'National Zoo',
      description: 'FREE zoo featuring giant pandas, lions, and over 2,700 animals.',
      distance: '20 min drive',
      category: 'family',
      highlight: 'Free Entry',
      tickets: 'https://nationalzoo.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Zoo+Washington+DC'
    }
  ];

  // Petworth/Columbia Heights area recommendations  
  const petworthPlaces = [
    // Restaurants
    {
      name: 'Timber Pizza Company',
      description: 'Award-winning wood-fired pizzas with creative toppings and craft cocktails.',
      distance: '5 min walk',
      category: 'restaurants',
      highlight: 'Local Favorite',
      website: 'https://www.timberpizza.com/',
      directions: 'https://maps.google.com/?q=Timber+Pizza+Company+Petworth+DC'
    },
    {
      name: 'Primrose',
      description: 'French-inspired bistro with excellent wine selection and intimate atmosphere.',
      distance: '5 min walk',
      category: 'restaurants',
      website: 'https://www.primrosedc.com/',
      directions: 'https://maps.google.com/?q=Primrose+Petworth+DC'
    },
    {
      name: 'Hitching Post',
      description: 'Neighborhood bar with great craft beers and casual American fare.',
      distance: '3 min walk',
      category: 'restaurants',
      website: 'https://www.hitchingpostdc.com/',
      directions: 'https://maps.google.com/?q=Hitching+Post+Petworth+DC'
    },
    {
      name: 'Upshur Street Books & Cafe',
      description: 'Cozy bookstore cafe perfect for morning coffee and browsing.',
      distance: '5 min walk',
      category: 'restaurants',
      directions: 'https://maps.google.com/?q=Upshur+Street+Books+DC'
    },
    {
      name: 'Call Your Mother',
      description: "Famous Jew-ish deli known for incredible bagels and sandwiches.",
      distance: '10 min walk',
      category: 'restaurants',
      highlight: 'Instagram Famous',
      website: 'https://www.callyourmotherdeli.com/',
      directions: 'https://maps.google.com/?q=Call+Your+Mother+DC'
    },
    // Sightseeing
    {
      name: 'Rock Creek Park',
      description: 'Expansive urban park with hiking trails, picnic areas, and nature center.',
      distance: '10 min drive',
      category: 'sightseeing',
      highlight: 'Nature Escape',
      website: 'https://www.nps.gov/rocr/',
      directions: 'https://maps.google.com/?q=Rock+Creek+Park+Washington+DC'
    },
    {
      name: 'National Mall',
      description: 'All the iconic monuments - Lincoln Memorial, Washington Monument, WWII Memorial.',
      distance: '15 min drive',
      category: 'sightseeing',
      highlight: 'Must Visit',
      directions: 'https://maps.google.com/?q=National+Mall+Washington+DC'
    },
    {
      name: 'Lincoln Memorial',
      description: 'Iconic monument honoring Abraham Lincoln with stunning views of the Reflecting Pool.',
      distance: '20 min drive',
      category: 'sightseeing',
      highlight: 'Iconic Landmark',
      website: 'https://www.nps.gov/linc/',
      directions: 'https://maps.google.com/?q=Lincoln+Memorial+Washington+DC'
    },
    // Museums
    {
      name: 'Smithsonian Museums',
      description: 'All museums are FREE. Best accessed via Metro (15-20 min ride).',
      distance: '20 min Metro',
      category: 'museums',
      highlight: 'Free Entry',
      tickets: 'https://www.si.edu/visit',
      directions: 'https://maps.google.com/?q=Smithsonian+Institution+Washington+DC'
    },
    {
      name: 'National Portrait Gallery',
      description: 'Portraits of notable Americans in stunning historic building.',
      distance: '15 min Metro',
      category: 'museums',
      highlight: 'Free Entry',
      tickets: 'https://npg.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Portrait+Gallery+Washington+DC'
    },
    {
      name: 'National Museum of African American History',
      description: 'Powerful museum documenting African American history and culture.',
      distance: '20 min Metro',
      category: 'museums',
      highlight: 'Timed Entry',
      tickets: 'https://nmaahc.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Museum+of+African+American+History+DC'
    },
    // Family
    {
      name: 'Petworth Recreation Center',
      description: 'Large playground, basketball courts, and community pool (summer).',
      distance: '5 min walk',
      category: 'family',
      directions: 'https://maps.google.com/?q=Petworth+Recreation+Center+DC'
    },
    {
      name: 'National Zoo',
      description: 'Free zoo featuring giant pandas and thousands of animals. Family favorite!',
      distance: '10 min drive',
      category: 'family',
      highlight: 'Free Entry',
      tickets: 'https://nationalzoo.si.edu/visit',
      directions: 'https://maps.google.com/?q=National+Zoo+Washington+DC'
    }
  ];

  const places = isCapitolHill ? capitolHillPlaces : petworthPlaces;
  
  const filteredPlaces = activeCategory === 'all' 
    ? places 
    : places.filter(p => p.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Property Not Found</h2>
          <Link to="/properties">
            <Button className="bg-slate-800 hover:bg-slate-700 text-white">View All Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link to={`/property/${id}`} className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to {property.name}
        </Link>

        {/* Header */}
        <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <img 
              src={property.images[0]} 
              alt={property.name}
              className="w-24 h-24 object-cover rounded-lg"
            />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Local Guide for {property.name}
              </h1>
              <div className="flex items-center text-slate-600">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{property.location}</span>
              </div>
              <p className="text-slate-500 mt-2">
                {isCapitolHill 
                  ? 'Located in historic Capitol Hill - walking distance to monuments, museums, and great dining'
                  : 'Located in vibrant Petworth - a neighborhood gem with local eateries and easy access to all of DC'
                }
              </p>
            </div>
            <Link to={`/property/${id}`}>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Home className="h-4 w-4 mr-2" />
                View Property
              </Button>
            </Link>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setActiveCategory(cat.id)}
                className={activeCategory === cat.id 
                  ? 'bg-slate-800 text-white' 
                  : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }
              >
                <Icon className="h-4 w-4 mr-2" />
                {cat.name}
              </Button>
            );
          })}
        </div>

        {/* Places Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlaces.map((place, idx) => (
            <Card key={idx} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-lg text-slate-900">{place.name}</h3>
                  {place.highlight && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">
                      <Star className="h-3 w-3 mr-1" />
                      {place.highlight}
                    </Badge>
                  )}
                </div>
                <p className="text-slate-600 text-sm mb-4">{place.description}</p>
                
                {/* Distance */}
                <div className="flex items-center text-sm text-slate-500 mb-4">
                  <Clock className="h-4 w-4 mr-1" />
                  {place.distance}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {place.tickets && (
                    <a 
                      href={place.tickets} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <Ticket className="h-4 w-4" />
                      Get Tickets
                    </a>
                  )}
                  {place.website && !place.tickets && (
                    <a 
                      href={place.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Website
                    </a>
                  )}
                  {place.directions && (
                    <a 
                      href={place.directions} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <Map className="h-4 w-4" />
                      Directions
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Getting Around Section */}
        <Card className="mt-10">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <Navigation className="h-5 w-5 mr-2" />
              Getting Around from {isCapitolHill ? 'Capitol Hill' : 'Petworth'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Metro</h3>
                <p className="text-sm text-slate-600 mb-3">
                  {isCapitolHill 
                    ? 'Eastern Market (Orange/Blue/Silver) is 3 min walk. Capitol South station also nearby.'
                    : 'Georgia Ave-Petworth (Green/Yellow) is 5-10 min walk. Easy access to downtown.'
                  }
                </p>
                <a 
                  href="https://www.wmata.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  WMATA Metro Info →
                </a>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Rideshare</h3>
                <p className="text-sm text-slate-600">
                  Uber/Lyft widely available. Most DC destinations are 10-20 min and $10-20.
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Parking</h3>
                <p className="text-sm text-slate-600">
                  {isCapitolHill 
                    ? 'Free street parking available. Check signs for cleaning days.'
                    : 'Free street parking and on-premises parking available at property.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View All Properties CTA */}
        <div className="mt-10 text-center">
          <Link to="/local-guide">
            <Button variant="outline" className="mr-4">
              View General DC Guide
            </Button>
          </Link>
          <Link to="/properties">
            <Button className="bg-slate-800 hover:bg-slate-700 text-white">
              Browse All Properties
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PropertyLocalGuide;
